import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setSignInOverlayIsOpen from "@insite/client-framework/Store/Components/SignInOverlay/Handlers/SetSignInOverlayIsOpen";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import Header from "@midland-industries/Components/Header/Header";
import HeaderNavigation from "@midland-industries/Components/Header/HeaderNavigation";
import MessageBanner from "@midland-industries/Components/Header/MessageBanner";
import PromiseBanner from "@midland-industries/Components/Header/PromiseBanner";
import { getCustomerLabel, getFulfillmentLabel } from "@midland-industries/Store/Context/XcContextSelectors";
import useDetectScroll from "@midland-industries/Utilities/ScrollDirection/useDetectScroll";
import React, { useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { ThemeProps, withTheme } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    return {
        isSignInPage: getCurrentPage(state).type === "SignInPage",
        userName: state.context.session.userName,
        currentUserIsGuest: getCurrentUserIsGuest(state),
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        currentLocation: getLocation(state),
        totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay,
        shouldLoadCart: !getCurrentCartState(state).value && !getCurrentCartState(state).isLoading,
        cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
        customerLabel: getCustomerLabel(state),
        fulfillmentLabel: getFulfillmentLabel(state),
    };
};

const mapDispatchToProps = {
    signOut,
    setSignInOverlayIsOpen,
    loadCurrentCart,
};

type Props = WidgetProps &
    ReturnType<typeof mapStateToProps> &
    HasHistory &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme>;

const CustomHeader: React.FC<Props> = ({
    userName,
    currentUserIsGuest,
    isSignInPage,
    theme,
    setSignInOverlayIsOpen,
    signInUrl,
    currentLocation,
    history,
    totalCountDisplay,
    shouldLoadCart,
    loadCurrentCart,
    cartUrl,
    customerLabel,
    fulfillmentLabel,
}) => {
    const [cartCount, setCartCount] = useState(0);
    const [upStartPosition, setUpStartPosition] = useState<number | undefined>();
    const [isVisible, setIsVisible] = useState(true);
    const { scrollDir, scrollPosition } = useDetectScroll();
    const isLoggedIn = !!userName && !currentUserIsGuest;
    const message = "";
    const lastScrollDir = useRef("still");

    useEffect(() => {
        if (scrollPosition.top > 188 && isVisible && scrollDir === "down" && lastScrollDir.current === "down") {
            setIsVisible(false);
        }

        if (scrollPosition.top < 68 && !isVisible) {
            setIsVisible(true);
        }

        if (scrollDir === "up" && lastScrollDir.current === "up" && !isVisible && !upStartPosition) {
            setUpStartPosition(scrollPosition.top);
        }

        if (scrollDir === "up" && !isVisible && upStartPosition) {
            if (upStartPosition - 50 >= scrollPosition.top) {
                setIsVisible(true);
                setUpStartPosition(undefined);
            }
        }

        if (scrollDir === "down") {
            setUpStartPosition(undefined);
        }

        lastScrollDir.current = scrollDir;
    }, [scrollDir, scrollPosition, upStartPosition]);

    useEffect(() => {
        if (shouldLoadCart) {
            loadCurrentCart({ shouldLoadFullCart: false, replaceCart: false });
        }
    }, [shouldLoadCart]);

    useEffect(() => {
        setCartCount(totalCountDisplay || 0);
    }, [totalCountDisplay]);

    const onSignInHandler = (e: React.MouseEvent) => {
        if ((!userName || currentUserIsGuest) && !isSignInPage) {
            e.preventDefault();

            if (theme.signIn?.useOverlay) {
                setSignInOverlayIsOpen({ isOpen: true });
                return;
            }

            if (!signInUrl) {
                logger.warn("No url was found for SignInPage, defaulting to /SignIn");
                history.push("/MyAccount/SignIn");
            } else {
                const returnUrl = currentLocation.pathname + currentLocation.search;
                if (returnUrl === "/") {
                    history.push(`${signInUrl}`);
                } else {
                    history.push(`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`);
                }
            }
        }
    };

    return (
        <>
            <div style={{ maxWidth: 1112, margin: "0 auto", padding: "24px 0", position: "relative", zIndex: 2 }}>
                <Header
                    userName={userName}
                    currentUserIsGuest={currentUserIsGuest}
                    cartUrl={cartUrl || "/cart"}
                    cartCount={cartCount}
                    onSignInHandler={onSignInHandler}
                />
                {isVisible ? (
                    <HeaderNavigation
                        isLoggedIn={isLoggedIn}
                        isVisible={isVisible}
                        customerLabel={customerLabel}
                        fulfillmentLabel={fulfillmentLabel}
                    />
                ) : (
                    <></>
                )}
            </div>
            {isLoggedIn && message && isVisible ? <MessageBanner message={message} /> : <></>}
            {!isLoggedIn && isVisible ? <PromiseBanner /> : <></>}
        </>
    );
};

const customHeaderWidget: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(CustomHeader))),
    definition: {
        displayName: "Custom Header",
        group: "Header",
    },
};

export default customHeaderWidget;
